import { Icon } from '@d-edge/overnight-hotelier-react'
import type { ReactNode } from 'react'
import { Icons } from '@/components/ui/Icons'
import { AREA_ORDER, summarise, type Area, type AttentionItem, type Severity } from './attention'
import styles from './AttentionDialog.module.css'
import { SEVERITY, tileIcon, tileLabel, tileValue } from './overview'

const SEVERITIES: Severity[] = ['critical', 'warning', 'info']
const TOTAL_CLASS: Record<Severity, string> = { critical: styles.totalCritical, warning: styles.totalWarning, info: styles.totalInfo }
const SEGMENT_CLASS: Record<Severity, string> = { critical: styles.segCritical, warning: styles.segWarning, info: styles.segInfo }

// The product icon of each panel; RateScreener keeps the spider drawn elsewhere in the app.
const AREA_ICON: Record<Area, ReactNode> = {
  RateScreener: <Icons.Spider size={14} />,
  Sentinel: <Icon name="eye" size="small" />,
  'Price recommendation': <Icon name="tags" size="small" />,
  Proxies: <Icon name="globe" size="small" />,
  'Data sources': <Icon name="table" size="small" />,
}

interface AttentionOverviewProps {
  items: AttentionItem[]
  onSelect: (item: AttentionItem) => void
}

/** The whole situation on one screen: severity totals, then a panel per product holding one tile per item. */
export function AttentionOverview({ items, onSelect }: AttentionOverviewProps) {
  const counts = summarise(items)
  const panels = AREA_ORDER.map((area) => ({ area, items: items.filter((i) => i.area === area) })).filter((p) => p.items.length > 0)
  const description = SEVERITIES.map((s) => `${counts[s]} ${SEVERITY[s].label.toLowerCase()}`).join(', ')

  return (
    <div className={styles.overview}>
      <div className={styles.totals}>
        {SEVERITIES.map((severity) => (
          <div key={severity} className={`${styles.total} ${TOTAL_CLASS[severity]}`}>
            <span className={styles.totalValue}>{counts[severity]}</span>
            <span className={styles.totalLabel}>{SEVERITY[severity].label}</span>
          </div>
        ))}
        <div className={styles.bar} role="img" aria-label={description} title={description}>
          {SEVERITIES.map((severity) => counts[severity] > 0 && <span key={severity} className={SEGMENT_CLASS[severity]} style={{ flexGrow: counts[severity] }} />)}
        </div>
      </div>

      <div className={styles.board}>
        {panels.map((panel) => (
          <section key={panel.area} className={styles.panel}>
            <h3 className={styles.panelTitle}>
              <span className={styles.panelIcon}>{AREA_ICON[panel.area]}</span>
              {panel.area}
              <span className={styles.panelCount}>{panel.items.length}</span>
            </h3>
            <div className={styles.tiles}>
              {panel.items.map((item) => (
                <button key={item.id} type="button" className={`${styles.tile} ${styles[item.severity]}`} onClick={() => onSelect(item)} title={`${item.title} · open the details`}>
                  <span className={styles.tileTop}>
                    <Icon name={tileIcon(item)} size="small" className={styles.tileIcon} />
                    <span className={styles.tileValue}>{tileValue(item)}</span>
                  </span>
                  <span className={styles.tileLabel}>{tileLabel(item)}</span>
                  <span className={styles.tileSeverity}>{SEVERITY[item.severity].label}</span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}
